import React, { memo, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";

interface HeroProps {
  title?: string;
  subtitle?: string;
  description?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  image?: string;
  className?: string;
}

const SLIDE_IMAGES = [
  "/assets/slide-1.jpeg",
  "/assets/slide-2.jpeg",
  "/assets/slide-3.jpeg",
];

/**
 * Modern Hero component with split layout (content left, visual right)
 * Designed for community websites with lean and sleek aesthetics
 */
const Hero: React.FC<HeroProps> = memo(
  ({
    title,
    subtitle,
    description,
    primaryButtonText,
    secondaryButtonText,
    className = "",
  }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { currentLanguage } = useSelector(
      (state: RootState) => state.language
    );
    const [isLoaded, setIsLoaded] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
      setIsLoaded(true);
    }, []);

    useEffect(() => {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % SLIDE_IMAGES.length);
      }, 5000);
      return () => clearInterval(timer);
    }, []);

    return (
      <section
        className={`relative min-h-[85vh] flex items-center overflow-hidden bg-gradient-to-br from-white via-emerald-50/50 to-stone-100 dark:from-gray-900 dark:via-emerald-900/10 dark:to-stone-900/20 ${className}`}
      >
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-100/40 dark:bg-emerald-900/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-stone-200/40 dark:bg-stone-800/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Side - Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={isLoaded ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="space-y-8 flex flex-col items-center lg:items-start text-center lg:text-left"
            >
              {/* Badge / Welcome Text */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={
                  isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
                }
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center gap-3 px-6 py-3 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-emerald-800 dark:text-emerald-300 rounded-full text-base sm:text-lg font-bold border border-emerald-100 dark:border-emerald-900/50 shadow-sm"
              >
                <img
                  src="/assets/ddc-logo.jpeg"
                  alt="DDC Logo"
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span>{t("hero.badge", "Welcome to Djichami Community")}</span>
              </motion.div>

              {/* Title */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={
                  isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }
                }
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold text-gray-900 dark:text-white leading-tight tracking-tight"
              >
                {title || t("hero.title", "Building a Thriving Community")}
                <span className="block mt-2 bg-gradient-to-r from-emerald-600 via-green-600 to-stone-600 bg-clip-text text-transparent">
                  {subtitle || t("hero.subtitle", "Together")}
                </span>
              </motion.h1>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={
                  isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
                }
                transition={{ duration: 0.8, delay: 0.5 }}
                className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-2xl"
              >
                {description ||
                  t(
                    "hero.description",
                    "Join us in creating positive change, fostering connections, and building a stronger, more vibrant community for everyone."
                  )}
              </motion.p>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={
                  isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
                }
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-wrap justify-center lg:justify-start gap-8 pt-4"
              >
                <div>
                  <div className="text-3xl md:text-4xl font-bold text-emerald-600 dark:text-emerald-500">
                    500+
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {t("hero.stats.members", "Community Members")}
                  </div>
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-bold text-stone-600 dark:text-stone-400">
                    50+
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {t("hero.stats.projects", "Active Projects")}
                  </div>
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-bold text-green-600 dark:text-green-500">
                    100+
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {t("hero.stats.events", "Events Hosted")}
                  </div>
                </div>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={
                  isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
                }
                transition={{ duration: 0.8, delay: 0.7 }}
                className="flex flex-col sm:flex-row gap-4 pt-4 w-full sm:w-auto"
              >
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate(`/${currentLanguage}/projects`)}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold text-lg rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 w-full sm:w-auto"
                >
                  <span>
                    {primaryButtonText ||
                      t("hero.primaryButton", "Get Involved")}
                  </span>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate(`/${currentLanguage}/about`)}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-bold text-lg rounded-2xl border-2 border-gray-200 dark:border-gray-700 shadow-md hover:shadow-lg transition-all duration-300 w-full sm:w-auto"
                >
                  <span>
                    {secondaryButtonText ||
                      t("hero.secondaryButton", "Learn More")}
                  </span>
                </motion.button>
              </motion.div>
            </motion.div>

            {/* Right Side - Visual (Slider) */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={isLoaded ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative lg:h-[600px] h-[400px]"
            >
              {/* Main Image Slider */}
              <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-2xl bg-gray-100 dark:bg-gray-800">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentSlide}
                    src={SLIDE_IMAGES[currentSlide]}
                    alt={title || "Community"}
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8 }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>

                {/* Slider Indicators */}
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                  {SLIDE_IMAGES.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${currentSlide === index
                        ? "bg-white w-8"
                        : "bg-white/50 hover:bg-white/80"
                        }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>

              {/* Floating Cards */}
              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={
                  isLoaded
                    ? { opacity: 1, y: 0, scale: 1 }
                    : { opacity: 0, y: 30, scale: 0.9 }
                }
                transition={{ duration: 0.8, delay: 0.8 }}
                className="absolute -bottom-6 -left-6 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-2xl border border-gray-100 dark:border-gray-700 max-w-xs hidden sm:block"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">🎯</span>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {t("hero.card.title", "Active Projects")}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t("hero.card.subtitle", "Making an impact")}
                    </div>
                  </div>
                </div>
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-500">
                  50+
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={
                  isLoaded
                    ? { opacity: 1, y: 0, scale: 1 }
                    : { opacity: 0, y: 30, scale: 0.9 }
                }
                transition={{ duration: 0.8, delay: 1 }}
                className="absolute -top-6 -right-6 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-2xl border border-gray-100 dark:border-gray-700 max-w-xs hidden sm:block"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-stone-400 to-emerald-500 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">🎉</span>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {t("hero.card2.title", "Upcoming Events")}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t("hero.card2.subtitle", "Join us soon")}
                    </div>
                  </div>
                </div>
                <div className="text-2xl font-bold text-stone-600 dark:text-stone-500">
                  12+
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isLoaded ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-gray-400 dark:border-gray-600 rounded-full flex items-start justify-center p-2"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-600 rounded-full"
            />
          </motion.div>
        </motion.div>
      </section>
    );
  }
);

Hero.displayName = "Hero";

export default Hero;
